import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, RotateCw, Beaker, Calendar, Target, MessageSquareQuote } from "lucide-react";
import { TRANSLATIONS } from "@/i18n";
import {
  FADE_IN_INITIAL,
  FADE_IN_ANIMATE,
  FADE_OUT_EXIT,
  SLIDE_UP_INITIAL,
  SLIDE_UP_ANIMATE,
  SLIDE_UP_TRANSITION,
} from "@/constants/diplomate";

/**
 * AntidoteResultPanel — 4 sections structurées :
 *   EN CLAIR / CE QU'ON TE DEMANDE / POUR QUAND / JARGON DÉTECTÉ.
 * Le backend garantit :
 *   - deadline = « Pas précisé. » si aucune date n'est présente dans le message d'origine ;
 *   - jargon = uniquement les expressions RÉELLEMENT présentes.
 */
export const AntidoteResultPanel = forwardRef(function AntidoteResultPanel(
  { lang, result, loading, copied, onCopy, onRegenerate },
  ref,
) {
  const t = TRANSLATIONS[lang];
  const jargon = result?.jargon || [];
  const hasResult = !!result;

  const combinedForCopy = hasResult
    ? [
        `${t.antidoteSections.plain}\n${result.plain}`,
        `\n${t.antidoteSections.ask}\n${result.ask}`,
        `\n${t.antidoteSections.deadline}\n${result.deadline || t.antidoteNoDeadline}`,
        jargon.length
          ? `\n${t.antidoteSections.jargon}\n${jargon.join(", ")}`
          : "",
      ].join("\n")
    : "";

  return (
    <div
      ref={ref}
      data-testid="antidote-panel"
      className="border-2 border-black nb-shadow relative flex flex-col"
      style={{ background: hasResult ? "#C7CEEA22" : "#FFFFFF" }}
    >
      <div className="flex items-center justify-between border-b-2 border-black px-5 py-3 bg-[#F3EFE7]">
        <div className="flex items-center gap-2">
          <Beaker className="w-3.5 h-3.5 text-black" />
          <span className="font-mono text-[11px] uppercase tracking-widest font-bold">
            {t.antidoteResultLabel}
          </span>
        </div>
        {hasResult && !loading && (
          <div className="flex items-center gap-2">
            <button
              data-testid="antidote-regenerate-btn"
              onClick={onRegenerate}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              <RotateCw className="w-3 h-3" /> {t.again}
            </button>
            <button
              data-testid="antidote-copy-btn"
              onClick={() => onCopy(combinedForCopy)}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" /> {t.copied}
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" /> {t.copy}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="min-h-[240px] sm:min-h-[300px] relative px-5 py-5">
        <AnimatePresence mode="wait">
          {loading && (
            <motion.div
              key="loading"
              initial={FADE_IN_INITIAL}
              animate={FADE_IN_ANIMATE}
              exit={FADE_OUT_EXIT}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
            >
              <div className="flex gap-2">
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" />
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.15s" }} />
                <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.3s" }} />
              </div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                {t.generating}
              </p>
            </motion.div>
          )}

          {!loading && hasResult && (
            <motion.div
              key="result"
              initial={SLIDE_UP_INITIAL}
              animate={SLIDE_UP_ANIMATE}
              transition={SLIDE_UP_TRANSITION}
              className="flex flex-col gap-5"
              data-testid="antidote-result"
            >
              <Section icon={<MessageSquareQuote className="w-3 h-3" strokeWidth={2.5} />} label={t.antidoteSections.plain}>
                <p className="whitespace-pre-wrap text-base leading-[1.6] text-black">
                  {result.plain}
                </p>
              </Section>

              <Section icon={<Target className="w-3 h-3" strokeWidth={2.5} />} label={t.antidoteSections.ask}>
                <p className="whitespace-pre-wrap text-base leading-[1.6] text-black font-medium">
                  {result.ask}
                </p>
              </Section>

              <Section icon={<Calendar className="w-3 h-3" strokeWidth={2.5} />} label={t.antidoteSections.deadline}>
                <p
                  className={`text-base leading-[1.6] ${
                    result.deadline && result.deadline !== t.antidoteNoDeadline
                      ? "text-black font-bold"
                      : "text-[#4A4A4A] italic"
                  }`}
                  data-testid="antidote-deadline"
                >
                  {result.deadline || t.antidoteNoDeadline}
                </p>
              </Section>

              <Section icon={null} label={t.antidoteSections.jargon}>
                {jargon.length ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="antidote-jargon">
                    {jargon.map((term, i) => (
                      <span
                        key={`${term}-${i}`}
                        className="font-mono text-[11px] bg-white border border-black px-2 py-0.5"
                      >
                        {term}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm italic text-[#4A4A4A]">{t.antidoteNoJargon}</p>
                )}
              </Section>
            </motion.div>
          )}

          {!loading && !hasResult && (
            <motion.div
              key="empty"
              initial={FADE_IN_INITIAL}
              animate={FADE_IN_ANIMATE}
              className="absolute inset-0 flex items-center justify-center text-center px-6"
            >
              <p className="text-[#4A4A4A] text-sm italic max-w-xs leading-relaxed">
                {t.antidoteOutputEmpty}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

const Section = ({ icon, label, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-1.5">
      {icon ? (
        <div className="w-5 h-5 bg-white border-2 border-black flex items-center justify-center shrink-0">
          {icon}
        </div>
      ) : null}
      <span className="font-mono text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]">
        {label}
      </span>
      <span className="flex-1 h-px bg-black/15" />
    </div>
    <div className={icon ? "pl-7" : ""}>{children}</div>
  </div>
);
